import type { SkillData } from '../spacetime/modules/SkillModule';
import type { ActionTooltip } from './ActionTooltip';
import { buildSkillSlotEl, showSkillTooltip } from './skillSlot';

export interface MetaSkillPickerOptions {
  anchorEl: HTMLElement;
  subSkills: SkillData[];
  onSelect: (skillId: string) => void;
  onDismiss: () => void;
  getTick: () => number;
  tooltip: ActionTooltip;
}

export function createMetaSkillPicker(options: MetaSkillPickerOptions): { destroy(): void; render(): void } {
  const { anchorEl, subSkills, onSelect, onDismiss, getTick, tooltip } = options;

  const card = document.createElement('div');
  card.style.cssText = `
    position: fixed;
    background: #1a1f2e;
    border: 1px solid #3a4354;
    border-radius: 6px;
    padding: 4px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.5);
    z-index: 10000;
    pointer-events: auto;
  `;

  // Track clickable (non-cooldown) skills for number-key activation
  // Indexed by visual slot position; null means on cooldown (key press does nothing)
  let slotSkills: (string | null)[] = [];

  function buildSlots(): void {
    card.innerHTML = '';
    slotSkills = [];
    const currentTick = getTick();
    subSkills.forEach((skill, i) => {
      const { el, onCooldown } = buildSkillSlotEl(skill, currentTick, i + 1, {
        onClick: () => { destroy(); onSelect(skill.skillId); },
        onTooltipShow: (s, el) => { showSkillTooltip(tooltip, s, el); },
        onTooltipHide: () => { tooltip.hide(); },
      });
      slotSkills.push(onCooldown ? null : skill.skillId);
      card.appendChild(el);
    });
  }

  buildSlots();

  document.body.appendChild(card);

  // Position below the anchor element
  const anchorRect = anchorEl.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  let left = anchorRect.left + anchorRect.width / 2 - cardRect.width / 2;
  let top = anchorRect.bottom + 6;

  if (left < 4) left = 4;
  if (left + cardRect.width > window.innerWidth - 4) left = window.innerWidth - 4 - cardRect.width;
  if (top + cardRect.height > window.innerHeight - 4) top = anchorRect.top - cardRect.height - 6;

  card.style.left = `${left}px`;
  card.style.top = `${top}px`;

  let destroyed = false;

  function onOutsideClick(e: MouseEvent): void {
    if (!card.contains(e.target as Node)) {
      destroy();
      onDismiss();
    }
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      destroy();
      onDismiss();
      return;
    }
    const num = parseInt(e.key, 10);
    if (num >= 1 && num <= 9) {
      const skillId = slotSkills[num - 1];
      if (skillId) {
        e.preventDefault();
        e.stopPropagation();
        destroy();
        onSelect(skillId);
      }
    }
  }

  // Defer so the opening click doesn't immediately close
  setTimeout(() => {
    document.addEventListener('mousedown', onOutsideClick, true);
    document.addEventListener('keydown', onKeyDown, true);
  }, 0);

  function destroy(): void {
    if (destroyed) return;
    destroyed = true;
    document.removeEventListener('mousedown', onOutsideClick, true);
    document.removeEventListener('keydown', onKeyDown, true);
    card.remove();
  }

  return { destroy, render: buildSlots };
}
